// AI Step 2: Use Case & Context
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Brain } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { SECTORS, AFFECTED_POPULATIONS } from '../constants';

export default function Step2UseCase({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Use Case & Context</h2>
        <p className="text-muted-foreground mt-1">Define the purpose and context of AI system use (NIST MAP function).</p>
      </div>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Brain className="h-4 w-4 text-primary" />Purpose & Users</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Purpose of AI System <span className="text-destructive">*</span></Label>
            <Textarea value={formData.purpose || ''} onChange={e => updateField('purpose', e.target.value)} placeholder="What is the AI system used for? What problem does it solve?" className="mt-1.5" rows={3} data-testid="ai-purpose" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Sector/Domain</Label>
              <Select value={formData.sector_domain} onValueChange={v => updateField('sector_domain', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select sector" /></SelectTrigger>
                <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Deployment Context</Label>
              <Input value={formData.deployment_context || ''} onChange={e => updateField('deployment_context', e.target.value)} placeholder="e.g., Customer support portal" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Intended Users</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {['Internal Staff', 'Managers', 'Customers', 'Partners', 'General Public'].map(user => (
                <button key={user} type="button"
                  onClick={() => { const updated = formData.intended_users.includes(user) ? formData.intended_users.filter(u => u !== user) : [...formData.intended_users, user]; updateField('intended_users', updated); }}
                  className={cn("px-3 py-1.5 rounded-full text-sm border transition-all", formData.intended_users.includes(user) ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:border-primary/50")}>
                  {user}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Affected Populations</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {AFFECTED_POPULATIONS.map(pop => (
                <button key={pop} type="button"
                  onClick={() => { const updated = formData.affected_populations.includes(pop) ? formData.affected_populations.filter(p => p !== pop) : [...formData.affected_populations, pop]; updateField('affected_populations', updated); }}
                  className={cn("px-3 py-1.5 rounded-full text-sm border transition-all", formData.affected_populations.includes(pop) ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:border-primary/50")}>
                  {pop}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Expected Benefits</Label>
            <Textarea value={formData.expected_benefits || ''} onChange={e => updateField('expected_benefits', e.target.value)} placeholder="What benefits does this AI system provide?" className="mt-1.5" rows={2} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
